<template>
  <div class="category-picker min-h-[32px]" ref="referenceRef">
    <!-- Seçim Alanı - Minimal Tasarım -->
    <div @click="isOpen ? toggleDropdown(false) : toggleDropdown(true)" class="w-full border border-gray-300 rounded-md bg-white shadow-sm hover:border-primary/60 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary/60 transition-all flex items-center justify-between py-2 px-3 cursor-pointer" :class="{ 'border-red-500 hover:border-red-500 focus-within:border-red-500 focus-within:ring-red-200': error }">
      <div class="text-sm text-gray-700 truncate flex items-center">
        <span v-if="selectedCategories.length === 0" class="text-gray-500">{{ placeholder }}</span> <span v-else class="flex items-center font-medium">
          <span class="material-symbols-outlined text-primary text-base mr-1">category</span>
          {{ selectedCategories.length }} kategori seçildi
        </span>
      </div>
      <span class="material-symbols-outlined text-gray-500 !text-[18px] transition-transform ml-2" :class="{'rotate-180 text-primary': isOpen}">
        expand_more
      </span>
    </div>

    <!-- Hata Mesajı -->
    <p v-if="error" class="mt-1 text-xs text-red-600">{{ error }}</p>

    <!-- Dropdown Panel -->
    <Teleport to="body">
      <div v-if="isOpen" class="fixed bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden z-[99999]" ref="floatingRef" :style="{ ...floatingStyles, width: dropdownWidth, maxHeight: '270px' }" @loaded="checkDropdownPosition">
        <!-- Arama Kutusu (sticky) -->
        <div class="sticky top-0 z-20 bg-white p-2 border-b">
          <div class="relative">
            <input ref="searchInput" v-model="searchQuery" type="text" placeholder="Kategori ara..." class="w-full p-1.5 pl-7 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" @keydown.down.prevent="handleArrowDown" @keydown.up.prevent="handleArrowUp" @keydown.enter.prevent="selectHighlighted" @keydown.esc.prevent="toggleDropdown(false)"/>
            <span class="material-symbols-outlined absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 !text-[14px]">
              search
            </span>
            <button v-if="searchQuery" @click="searchQuery = ''" class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-full hover:bg-gray-100" type="button">
              <span class="material-symbols-outlined !text-[14px]">close</span>
            </button>
          </div>
        </div>

        <!-- Seçili Kategoriler Tabı (sticky) -->
        <div class="sticky top-[48px] z-10 flex items-center border-b bg-gray-50">
          <button @click="showSelectedOnly = false" class="flex-1 py-1.5 px-2 text-xs font-medium transition-colors flex items-center justify-center gap-1" :class="{ 'text-primary bg-white shadow-sm border-b-2 border-primary': !showSelectedOnly, 'text-gray-600 hover:bg-gray-100': showSelectedOnly }">
            <span class="material-symbols-outlined !text-[16px]">list</span> Tümü
          </button>
          <button @click="showSelectedOnly = true" class="flex-1 py-1.5 px-2 text-xs font-medium transition-colors flex items-center justify-center gap-1" :class="{ 'text-primary bg-white shadow-sm border-b-2 border-primary': showSelectedOnly, 'text-gray-600 hover:bg-gray-100': !showSelectedOnly }">
            <span class="material-symbols-outlined !text-[16px]">check_circle</span> Seçilenler ({{ selectedCategories.length }})
          </button>
        </div>

        <!-- Liste içeriği (scroll olacak) -->
        <div class="overflow-y-auto min-h-[120px] max-h-[200px]">
          <!-- Arama sonucu yok -->
          <div v-if="currentCategories.length === 0" class="p-4 text-center text-gray-500">
            <div class="flex flex-col items-center">
              <span class="material-symbols-outlined text-2xl text-gray-300 mb-2">search_off</span>
              <p class="text-xs">{{ showSelectedOnly ? 'Seçili kategori yok' : 'Kategori bulunamadı' }}</p>
            </div>
          </div>

          <!-- Kategoriler Listesi -->
          <div v-else>
            <div v-for="(category, index) in currentCategories" :key="category.id" @mousedown.prevent="selectCategory(category.id)" @mouseover="highlightedIndex = index" class="px-2 py-1.5 hover:bg-gray-50 cursor-pointer transition-colors flex items-center" :class="{'bg-primary/5': index === highlightedIndex}">
              <!-- Seçim butonu (Radio stil) -->
              <div class="flex-shrink-0">
                <div class="w-4 h-4 rounded-full border flex items-center justify-center transition-colors" :class="isSelected(category.id) ? 'border-primary bg-primary/10' : 'border-gray-300'">
                  <div v-if="isSelected(category.id)" class="w-2 h-2 rounded-full bg-primary"></div>
                </div>
              </div>

              <!-- Alt kategori için indent ve çizgi -->
              <div v-if="category.level > 0" class="flex-shrink-0 ml-1" :style="{ marginLeft: (category.level * 8) + 'px' }">
                <div class="h-full border-l border-dotted border-gray-200 mr-2"></div>
              </div>

              <!-- Kategori adı -->
              <span class="ml-2 text-xs" :class="{
                  'font-medium text-primary': isSelected(category.id), 
                  'font-medium': index === highlightedIndex,
                  'text-gray-700': !isSelected(category.id) && index !== highlightedIndex
                }">
                {{ category.label }}
              </span>

              <!-- Alt kategorisi olduğunu belirten ikon -->
              <span v-if="hasChildren(category.id)" class="material-symbols-outlined ml-1 !text-[12px] text-gray-400" title="Alt kategorileri var">
                subdirectory_arrow_right
              </span>
            </div>
          </div>
        </div>

        <!-- Hepsini Temizle Butonu (Listenin altında) -->
        <div v-if="selectedCategories.length > 0" class="border-t border-gray-200 p-2 flex justify-center bg-gray-50">
          <button @click.stop="clearAllCategories()" class="w-full py-1.5 px-3 text-xs text-red-500 hover:text-red-600 bg-white hover:bg-red-50 transition-colors flex items-center justify-center gap-1 rounded border border-red-200 shadow-sm" title="Tüm kategorileri temizle">
            <span class="material-symbols-outlined !text-[16px]">delete</span> Tüm Seçimleri Temizle
          </button>
        </div>
      </div>
    </Teleport>

    <!-- Yeni Kategori Ekleme Butonu -->
    <div v-if="showAddButton && searchQuery && !matchingCategory && isOpen" class="mt-2">
      <button @click="addNewCategory" type="button" class="inline-flex items-center text-primary text-sm hover:underline focus:outline-none">
        <span class="material-symbols-outlined !text-[18px] mr-1">add_circle</span> <span>"{{ searchQuery }}" adlı yeni kategori ekle</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  categories: {
    type: Array,
    required: true,
  },
  placeholder: {
    type: String,
    default: 'Kategori seç',
  },
  error: {
    type: String,
    default: '',
  },
  showAddButton: {
    type: Boolean,
    default: false,
  },
  maxSelections: {
    type: Number,
    default: null, // sınırsız seçim
  },
})

const emit = defineEmits(['update:modelValue', 'add-new-category'])

// State variables
const selectedCategories = ref([...props.modelValue])
const searchQuery = ref('')
const isOpen = ref(false)
const searchInput = ref(null)
const highlightedIndex = ref(0)
const referenceRef = ref(null)
const floatingRef = ref(null)
const showSelectedOnly = ref(false) // Seçili kategoriler tabı için
const dropdownWidth = ref('200px')

// Floating UI setup
const { floatingStyles } = useFloating(referenceRef, floatingRef, {
  placement: 'bottom-start',
  middleware: [
    offset(4),
    flip({ padding: 8, boundary: document.body }), // Add boundary to ensure it stays within the viewport
    shift({ padding: 8, boundary: document.body }),
  ],
  whileElementsMounted: autoUpdate,
})

// Kategorilere ek bilgiler ekleyen fonksiyon
const processCategories = (categories, level = 0, parent = null) => {
  return categories.flatMap((category) => {
    // Ana kategori
    const processedCategory = {
      id: category.id || category.value,
      value: category.id || category.value,
      label: category.name || category.label,
      parentId: category.parentId || (parent ? parent.id : null),
      level,
    }

    // Alt kategorileri
    const children = category.children || []
    return [processedCategory, ...processCategories(children, level + 1, processedCategory)]
  })
}

// Tüm kategorileri işle, düz liste haline getir
const flattenedCategories = computed(() => {
  return processCategories(props.categories)
})

// Seçilmiş kategorilerin detayları
const selectedCategoriesWithDetails = computed(() => {
  return selectedCategories.value.map((id) => {
    const category = flattenedCategories.value.find(
        (c) => c.id === id || c.id === Number(id) || c.id === String(id),
    )
    return category || { id, label: `Kategori ${id}` }
  })
})

// Hiyerarşik olarak düzenlenmiş kategoriler
const arrangeHierarchical = (categories) => {
  // Önce tüm kategorileri bir Map'e yerleştir
  const categoryMap = new Map()
  categories.forEach(cat => {
    categoryMap.set(cat.id, { ...cat, children: [] })
  })

  // Parçalanmış ağaç yapısı listesi
  const parentGroups = []

  // Kategorileri parçalanmış ağaç yapısına düzenle
  categories.forEach(cat => {
    // Eğer parent yoksa veya bulunamıyorsa bu bir root kategoridir
    if (!cat.parentId || !categoryMap.has(cat.parentId)) {
      const rootNode = categoryMap.get(cat.id)
      // Sadece root seviyesindeki kategorileri ekle
      if (cat.level === 0) {
        parentGroups.push(rootNode)
      }
    } else {
      // Parent'a çocuk olarak ekle
      const parentNode = categoryMap.get(cat.parentId)
      if (parentNode) {
        parentNode.children.push(categoryMap.get(cat.id))
      }
    }
  })

  // Ağaç yapısını düz listeye çevir
  const result = []

  // Recursive olarak ağaç yapısını dolaş
  const flattenTree = (node, level = 0) => {
    result.push({ ...node, level })

    // Çocukları varsa, onları da ekle (sıralı olarak)
    if (node.children && node.children.length > 0) {
      // Çocukları alfabetik sırala
      const sortedChildren = [...node.children].sort((a, b) =>
          a.label.localeCompare(b.label),
      )

      sortedChildren.forEach(child => {
        flattenTree(child, level + 1)
      })
    }
  }

  // Önce parent kategorileri alfabetik sırala
  const sortedParents = [...parentGroups].sort((a, b) =>
      a.label.localeCompare(b.label),
  )

  // Her parent kategori için ağaç yapısını dolaş
  sortedParents.forEach(parent => {
    flattenTree(parent)
  })

  return result
}

// Aramaya göre filtrelenmiş kategoriler
const filteredCategories = computed(() => {
  let result = [...flattenedCategories.value]

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((category) =>
        category.label.toLowerCase().includes(query),
    )
  }

  // Hiyerarşik sıralama yap
  return arrangeHierarchical(result)
})

// Gösterilecek kategoriler (Seçimlere veya filtreye göre)
const currentCategories = computed(() => {
  if (showSelectedOnly.value) {
    // Sadece seçili kategorileri göster
    const selectedCats = flattenedCategories.value.filter(category => isSelected(category.id))
    return arrangeHierarchical(selectedCats) // Seçilen kategorileri de hiyerarşik göster
  } else {
    // Filtrelenen tüm kategorileri göster
    return filteredCategories.value
  }
})

// Arama terimiyle tam eşleşen kategori
const matchingCategory = computed(() => {
  if (!searchQuery.value) return null
  return flattenedCategories.value.find(
      (c) => c.label.toLowerCase() === searchQuery.value.toLowerCase(),
  )
})

// Kategori adını id'ye göre bulma
const getCategoryLabel = (categoryId) => {
  const category = flattenedCategories.value.find(c =>
      c.id === categoryId ||
      c.id === Number(categoryId) ||
      c.id === String(categoryId),
  )
  return category ? category.label : `Kategori ${categoryId}`
}

// Bir kategorinin alt kategorisi olup olmadığını kontrol et
const hasChildren = (categoryId) => {
  return flattenedCategories.value.some(c =>
      c.parentId === categoryId ||
      c.parentId === Number(categoryId) ||
      c.parentId === String(categoryId),
  )
}

// Seçili olup olmadığını kontrol et
const isSelected = (categoryId) => {
  return selectedCategories.value.some(id =>
      id === categoryId ||
      id === Number(categoryId) ||
      id === String(categoryId),
  )
}

// Kategori seçme
const selectCategory = (categoryId) => {
  if (isSelected(categoryId)) {
    // Seçiliyse kaldır
    removeCategory(categoryId)
  } else {
    // Değilse ekle - eğer maksimum sınırı aşmıyorsa
    if (props.maxSelections && selectedCategories.value.length >= props.maxSelections) {
      // Maksimuma ulaşıldıysa en eskiyi kaldır (tercih edilirse burada uyarı gösterilebilir)
      selectedCategories.value.shift()
    }
    selectedCategories.value.push(categoryId)
    updateModelValue()
  }

  // Seçim yapılınca arama metni temizlenir
  searchQuery.value = ''

  // Dropdown açık kalır, input focus alır
  nextTick(() => {
    searchInput.value?.focus()
    checkDropdownPosition()
  })
}

// Kategori kaldırma
const removeCategory = (categoryId) => {
  selectedCategories.value = selectedCategories.value.filter(
      (id) => id !== categoryId && id !== Number(categoryId) && id !== String(categoryId),
  )
  updateModelValue()
}

// Yeni kategori ekleme
const addNewCategory = () => {
  if (!searchQuery.value.trim()) return
  emit('add-new-category', searchQuery.value.trim())
  searchQuery.value = ''
}

// Dropdown'u aç/kapat
const toggleDropdown = (value) => {
  isOpen.value = value
  if (value) {
    highlightedIndex.value = 0
    updateDropdownWidth()
    nextTick(() => {
      if (floatingRef.value) {
        // Dropdown açıldıktan sonra genişliği tekrar ayarla
        updateDropdownWidth()
        // Pozisyonu kontrol et ve gerekirse ayarla
        checkDropdownPosition()
      }
    })
  }
}

// Dropdown genişliğini referans elemana göre ayarla
const updateDropdownWidth = () => {
  if (!referenceRef.value) return
  const rect = referenceRef.value.getBoundingClientRect()
  dropdownWidth.value = `${rect.width}px`
}

// Dropdown pozisyonunu kontrol et ve gerekirse ayarla
const checkDropdownPosition = () => {
  nextTick(() => {
    if (!floatingRef.value) return

    const dropdownRect = floatingRef.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    // Modal içinde görünümden dışarı çıkma durumunu kontrol et
    // En yakın modal veya diyalog elementini bul
    let modalElement = referenceRef.value.closest('.modal-content, .modal, [role="dialog"]')

    if (modalElement) {
      const modalRect = modalElement.getBoundingClientRect()
      const bottomSpace = modalRect.bottom - dropdownRect.top

      // Eğer dropdown modal dışına çıkıyorsa, üste konumlandır
      if (dropdownRect.bottom > modalRect.bottom - 20) {
        // Üst kısımda yeterli alan var mı kontrol et
        if (dropdownRect.height < referenceRef.value.getBoundingClientRect().top - modalRect.top) {
          floatingStyles.value = {
            ...floatingStyles.value,
            position: 'fixed',
            bottom: `${viewportHeight - referenceRef.value.getBoundingClientRect().top + 5}px`,
            top: 'auto',
          }
        } else {
          // Yeterli alan yoksa, yüksekliği sınırla
          floatingStyles.value = {
            ...floatingStyles.value,
            maxHeight: `${bottomSpace - 20}px`,
          }
        }
      }
    }
  })
}

// Click outside işleyicisi
const handleClickOutside = (event) => {
  if (isOpen.value &&
      referenceRef.value &&
      !referenceRef.value.contains(event.target) &&
      floatingRef.value &&
      !floatingRef.value.contains(event.target)) {
    toggleDropdown(false)
  }
}

// Aşağı ok tuşu
const handleArrowDown = () => {
  if (!isOpen.value) {
    toggleDropdown(true)
    return
  }
  if (filteredCategories.value.length === 0) return
  highlightedIndex.value = (highlightedIndex.value + 1) % filteredCategories.value.length
}

// Yukarı ok tuşu
const handleArrowUp = () => {
  if (!isOpen.value) {
    toggleDropdown(true)
    return
  }
  if (filteredCategories.value.length === 0) return
  highlightedIndex.value =
      (highlightedIndex.value - 1 + filteredCategories.value.length) %
      filteredCategories.value.length
}

// Tüm kategorileri temizle
const clearAllCategories = () => {
  selectedCategories.value = []
  updateModelValue()
  // Dropdown'u açık tut ama Tümü sekmesine geç
  showSelectedOnly.value = false
}

// Enter tuşuyla vurgulanan öğeyi seç
const selectHighlighted = () => {
  if (isOpen.value && filteredCategories.value.length > 0) {
    selectCategory(filteredCategories.value[highlightedIndex.value].id)
  }
}

// Model değerini güncelle
const updateModelValue = () => {
  emit('update:modelValue', selectedCategories.value)
}

// Props değişikliğini izle
watch(
    () => props.modelValue,
    (newValue) => {
      if (JSON.stringify(newValue) !== JSON.stringify(selectedCategories.value)) {
        selectedCategories.value = [...newValue]
      }
    },
    { deep: true },
)

// Arama terimine göre highlight'ı sıfırla
watch(searchQuery, () => {
  highlightedIndex.value = 0
})

// Komponent yüklendiğinde
onMounted(() => {
  // Modelvalue'dan gelen değerleri al
  selectedCategories.value = [...props.modelValue]

  // Click outside dinleyicisi ekle
  document.addEventListener('click', handleClickOutside)
  // FloatingUI handles scroll and resize events automatically
})

// Komponent kaldırıldığında
onUnmounted(() => {
  // Dinleyicileri temizle
  document.removeEventListener('click', handleClickOutside)
  // FloatingUI cleanup is automatic with whileElementsMounted
})
</script>

<style scoped>
.category-picker {
  position: relative;
  width: 100%;
}

/* Scrollbar görünümünü düzeltir */
div::-webkit-scrollbar {
  width: 8px;
}

div::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

div::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 4px;
}

div::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}
</style>